import { AlertCircle, Loader2, Send } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAssignmentsByProgramme } from "@/hooks/admin/assignments";
import { useAdHocSend, useEmailTemplates, usePreviewEmail } from "@/hooks/admin/emailTemplates";
import { useAdminProgrammes } from "@/hooks/admin/programmes";
import { api } from "@/services/api";
import type { UserWithEnrollment } from "@/types";

function formatReleaseDate(isoDate: string): string {
  const date = new Date(isoDate);
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "long" });
}

function formatStartDate(isoDate: string): string {
  const date = new Date(isoDate);
  return date.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
}

/** Replace empty variable values with a clearly wrong placeholder for preview. */
function buildPreviewContext(values: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(values).map(([k, v]) => [k, v.trim() || `⚠ [${k}]`]));
}

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function UserSearch({
  value,
  onSelect,
}: {
  value: UserWithEnrollment | null;
  onSelect: (u: UserWithEnrollment | null) => void;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<UserWithEnrollment[]>([]);
  const [loading, setLoading] = useState(false);
  const debouncedQuery = useDebounce(query, 300);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setResults([]);
      return;
    }
    setLoading(true);
    api
      .getUsersWithEnrollments({ q: debouncedQuery, page_size: 8 })
      .then((page) => setResults(page.items))
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [debouncedQuery]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  if (value) {
    return (
      <div className="flex items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm">
        <div>
          <p className="font-medium">{value.username}</p>
          {value.active_programme_name && (
            <p className="text-xs text-muted-foreground">{value.active_programme_name}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => onSelect(null)}
          className="ml-2 text-xs text-muted-foreground hover:text-foreground"
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <Input
        placeholder="Search by username…"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      {open && (query.trim() || loading) && (
        <div className="absolute z-10 mt-1 w-full rounded-md border border-input bg-background shadow-md">
          {loading ? (
            <div className="flex items-center justify-center p-3 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          ) : results.length === 0 ? (
            <p className="p-3 text-sm text-muted-foreground">No users found</p>
          ) : (
            results.map((u) => (
              <button
                key={u.id}
                type="button"
                className="w-full px-3 py-2 text-left text-sm hover:bg-accent"
                onClick={() => {
                  onSelect(u);
                  setOpen(false);
                  setQuery("");
                }}
              >
                <p className="font-medium">{u.username}</p>
                {u.active_programme_name && (
                  <p className="text-xs text-muted-foreground">{u.active_programme_name}</p>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export function AdHocSendTab() {
  const listQuery = useEmailTemplates();
  const programmesQuery = useAdminProgrammes();
  const preview = usePreviewEmail();
  const send = useAdHocSend();

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [values, setValues] = useState<Record<string, string>>({});
  const [targetType, setTargetType] = useState<"programme" | "user">("programme");
  const [selectedProgrammeId, setSelectedProgrammeId] = useState<string>("");
  const [selectedUser, setSelectedUser] = useState<UserWithEnrollment | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [sentCount, setSentCount] = useState<number | null>(null);

  const selectedTemplate = listQuery.data?.find((t) => t.id === selectedTemplateId);
  const editableVars = selectedTemplate?.variables.filter((v) => v !== "first_name") ?? [];

  const assignmentsQuery = useAssignmentsByProgramme(
    selectedProgrammeId || null,
    targetType === "programme" && Boolean(selectedProgrammeId)
  );

  // Reset variable values when template changes
  useEffect(() => {
    const template = listQuery.data?.find((t) => t.id === selectedTemplateId);
    if (template) {
      const vars = template.variables.filter((v) => v !== "first_name");
      setValues(Object.fromEntries(vars.map((v) => [v, ""])));
      setPreviewHtml(null);
      setSentCount(null);
    }
  }, [selectedTemplateId, listQuery.data]);

  // Auto-populate from selected cohort
  useEffect(() => {
    if (!selectedProgrammeId) return;
    const programme = programmesQuery.data?.find((p) => p.id === selectedProgrammeId);
    if (!programme) return;

    const firstAssignment = assignmentsQuery.data
      ?.filter((a) => a.week_number === 1 && a.assignment_number === 1)
      .at(0);

    setValues((prev) => ({
      ...prev,
      ...(prev.cohort_name !== undefined ? { cohort_name: programme.name } : {}),
      ...(prev.start_date !== undefined
        ? { start_date: formatStartDate(programme.start_date) }
        : {}),
      ...(prev.topic_release_date !== undefined && firstAssignment
        ? { topic_release_date: formatReleaseDate(firstAssignment.start_date) }
        : {}),
    }));
  }, [selectedProgrammeId, programmesQuery.data, assignmentsQuery.data]);

  const allVarsFilled = editableVars.every((v) => values[v]?.trim());

  function handlePreview() {
    if (!selectedTemplateId) return;
    preview.mutate(
      { id: selectedTemplateId, context: buildPreviewContext(values) },
      { onSuccess: (data) => setPreviewHtml(data.rendered_html) }
    );
  }

  function handleSend() {
    const targetId = targetType === "programme" ? selectedProgrammeId : (selectedUser?.id ?? "");
    if (!selectedTemplateId || !targetId) return;
    send.mutate(
      {
        template_id: selectedTemplateId,
        context: values,
        ...(targetType === "programme" ? { programme_id: targetId } : { user_id: targetId }),
      },
      { onSuccess: (data) => setSentCount(data.queued) }
    );
  }

  const canSend =
    Boolean(selectedTemplateId) &&
    allVarsFilled &&
    (targetType === "programme" ? Boolean(selectedProgrammeId) : Boolean(selectedUser));

  return (
    <div className="flex h-full flex-col gap-4 md:flex-row">
      {/* Form pane */}
      <div className="flex w-full flex-shrink-0 flex-col gap-5 overflow-y-auto rounded-lg border border-gray-200 bg-white p-5 md:w-80">
        {/* Step 1 — Send to */}
        <div className="flex flex-col gap-3">
          <Label>Step 1 — Send to</Label>
          <div className="flex gap-4 text-sm">
            {(["programme", "user"] as const).map((type) => (
              <label key={type} className="flex cursor-pointer items-center gap-1.5">
                <input
                  type="radio"
                  name="targetType"
                  value={type}
                  checked={targetType === type}
                  onChange={() => {
                    setTargetType(type);
                    setSelectedProgrammeId("");
                    setSelectedUser(null);
                  }}
                />
                {type === "programme" ? "Cohort" : "Single user"}
              </label>
            ))}
          </div>

          {targetType === "programme" ? (
            programmesQuery.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : (
              <>
                <Select value={selectedProgrammeId} onValueChange={setSelectedProgrammeId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a cohort…" />
                  </SelectTrigger>
                  <SelectContent>
                    {programmesQuery.data?.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                        {p.start_date
                          ? ` · ${new Date(p.start_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`
                          : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!selectedProgrammeId && (
                  <p className="text-xs text-muted-foreground">
                    Selecting a cohort will pre-fill cohort name, start date, and topic release
                    date.
                  </p>
                )}
              </>
            )
          ) : (
            <UserSearch value={selectedUser} onSelect={setSelectedUser} />
          )}
        </div>

        {/* Step 2 — Template */}
        <div className="flex flex-col gap-1.5">
          <Label>Step 2 — Template</Label>
          {listQuery.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : (
            <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a template…" />
              </SelectTrigger>
              <SelectContent>
                {listQuery.data?.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.id} — {t.category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Step 3 — Variables */}
        {selectedTemplate && editableVars.length > 0 && (
          <div className="flex flex-col gap-3">
            <Label>Step 3 — Variables</Label>
            <p className="text-xs text-muted-foreground">
              <code className="rounded bg-muted px-1 py-0.5 text-xs">first_name</code> is
              auto-populated from the recipient's profile.
              {targetType === "programme" && selectedProgrammeId && (
                <>
                  {" "}
                  Cohort name, start date, and topic release date were pre-filled from the selected
                  cohort.
                </>
              )}
            </p>
            {editableVars.map((v) => {
              const empty = !values[v]?.trim();
              return (
                <div key={v} className="flex flex-col gap-1.5">
                  <Label
                    htmlFor={`var-${v}`}
                    className={`text-xs ${empty ? "text-amber-600" : "text-muted-foreground"}`}
                  >
                    <code>{`{{ ${v} }}`}</code>
                    {empty && <span className="ml-1">⚠</span>}
                  </Label>
                  <Input
                    id={`var-${v}`}
                    value={values[v] ?? ""}
                    onChange={(e) => setValues((prev) => ({ ...prev, [v]: e.target.value }))}
                    placeholder={v}
                    className={
                      empty ? "border-amber-400 bg-amber-50 focus-visible:ring-amber-300" : ""
                    }
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* Actions */}
        {selectedTemplate && (
          <div className="flex flex-col gap-2">
            {!allVarsFilled && (
              <p className="text-xs text-amber-600">
                ⚠ {editableVars.filter((v) => !values[v]?.trim()).length} variable
                {editableVars.filter((v) => !values[v]?.trim()).length !== 1 ? "s" : ""} still
                missing
              </p>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={handlePreview}
              disabled={preview.isPending}
            >
              {preview.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Preview
            </Button>
            <Button type="button" onClick={handleSend} disabled={send.isPending || !canSend}>
              {send.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Send
            </Button>

            {sentCount !== null && (
              <p className="text-center text-sm text-green-600">
                Queued {sentCount} email{sentCount !== 1 ? "s" : ""}
              </p>
            )}
            {send.isError && (
              <div className="flex items-center gap-1.5 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {send.error instanceof Error ? send.error.message : "Send failed"}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Preview pane */}
      <div className="min-h-0 flex-1 rounded-lg border border-gray-200 bg-white p-5">
        {preview.isPending ? (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : preview.isError ? (
          <div className="flex h-full items-center justify-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            {preview.error instanceof Error ? preview.error.message : "Preview failed"}
          </div>
        ) : previewHtml ? (
          <iframe
            title="Email preview"
            srcDoc={previewHtml}
            className="h-full min-h-[480px] w-full rounded-lg border border-gray-200 bg-white"
            sandbox=""
          />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            Fill in the variables and click Preview
          </div>
        )}
      </div>
    </div>
  );
}
